import * as Sentry from '@sentry/browser';
import { isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, firebaseFunctions } from './init.js';
import { shouldClearStoredEmail } from '../../utils/auth-helpers.js';

// Check if running in development mode
const isDev = import.meta.env.DEV;

// Authentication state (simple object, will be wrapped in context)
const authState = {
    auth,
    user: null,
    authenticated: false,
    loading: true,
    error: null,
};

/**
 * One-shot check for current user that also writes through to the shared
 * authState. Resolves after the first onAuthStateChanged callback so callers
 * (notably router.handleRoute) can rely on authState being non-loading.
 */
const getCurrentUser = () =>
    new Promise((resolve, reject) => {
        const unsub = auth.onAuthStateChanged(
            (user) => {
                authState.user = user;
                authState.authenticated = !!user;
                authState.loading = false;
                resolve(user);
                unsub();
            },
            (error) => {
                authState.user = null;
                authState.authenticated = false;
                authState.loading = false;
                authState.error = error.message;
                reject(error);
                unsub();
            }
        );
    });

/**
 * Request a magic link email to be sent to the user
 * @param {string} email - User's email address
 * @param {string} redirectUrl - URL to redirect to after authentication
 * @returns {Promise<void>}
 */
const requestEmailLink = async (email, redirectUrl) => {
    if (isDev) {
        console.log('[AUTH] Requesting email link for:', email);
        console.log('[AUTH] Redirect URL:', redirectUrl);
    }

    if (!email || email.trim() === '') {
        throw new Error('Email address is required');
    }

    // Store email in localStorage BEFORE making the API call
    // This ensures the email is saved even if the API call fails/times out
    // but the email was actually sent by the backend
    window.localStorage.setItem('emailForSignIn', email);

    if (isDev) {
        console.log('[AUTH] Email stored in localStorage');
    }

    try {
        // Call existing Cloud Function (same as invoice-payment-app)
        const getSignInLink = httpsCallable(firebaseFunctions, 'getPaymentSignInLink');

        await getSignInLink({ email, redirectUrl });

        if (isDev) {
            console.log('[AUTH] Email link requested successfully');
        }
    } catch (error) {
        if (isDev) {
            console.error('[AUTH] Error requesting email link:', error);
        }
        // Note: We intentionally keep the email in localStorage even on error
        // because the backend might have sent the email before the error occurred
        throw error;
    }
};

/**
 * Check if the current URL is a magic link
 * @param {string} url - URL to check
 * @returns {boolean}
 */
const isEmailSignInLink = (url) => {
    const isLink = isSignInWithEmailLink(auth, url);
    if (isDev) {
        console.log('[AUTH] Checking if URL is email sign-in link:', url);
        console.log('[AUTH] Result:', isLink);
    }
    return isLink;
};

/**
 * Complete sign-in with email link
 * @param {string} email - User's email address (optional, will try to get from localStorage)
 * @returns {Promise<UserCredential>}
 */
const signInWithEmail = async (email) => {
    if (isDev) {
        console.log('[AUTH] Signing in with email link');
        console.log('[AUTH] Email provided:', email);
        console.log('[AUTH] Current URL:', window.location.href);
    }

    // Try to get email from localStorage if not provided
    let userEmail = email;
    if (!userEmail) {
        userEmail = window.localStorage.getItem('emailForSignIn');
        if (isDev) {
            console.log('[AUTH] Retrieved email from localStorage:', userEmail);
        }
    }

    if (!userEmail) {
        throw new Error('Email address not found. Please re-enter your email to complete sign-in.');
    }

    try {
        const result = await signInWithEmailLink(auth, userEmail, window.location.href);

        // Clear the email from localStorage after successful sign-in
        window.localStorage.removeItem('emailForSignIn');

        if (isDev) {
            console.log('[AUTH] Sign-in successful:', result.user.email);
        }

        return result;
    } catch (error) {
        if (isDev) {
            console.error('[AUTH] Sign-in error:', error);
        }

        // Only clear the stored email when Firebase tells us the link/email
        // itself is bad. Transient failures (network, internal) keep the email
        // so the user isn't bounced into the cross-browser-completion UI.
        if (shouldClearStoredEmail(error)) {
            window.localStorage.removeItem('emailForSignIn');
        }

        throw error;
    }
};

/**
 * Set up authentication state listener
 * This should be called once when the app initializes
 * @param {Function} onStateChange - Callback function when auth state changes
 * @returns {Function} Unsubscribe function
 */
const setupAuthListener = (onStateChange) => {
    if (isDev) {
        console.log('[AUTH] Setting up auth state listener');
    }

    return auth.onAuthStateChanged(
        (user) => {
            if (isDev) {
                console.log('[AUTH] Auth state changed:', user ? `User: ${user.email}` : 'No user');
            }

            authState.user = user;
            authState.authenticated = !!user;
            authState.loading = false;
            authState.error = null;

            // Call the callback if provided
            if (onStateChange) {
                onStateChange({
                    user,
                    authenticated: !!user,
                    loading: false,
                    error: null,
                });
            }
        },
        (error) => {
            if (isDev) {
                console.error('[AUTH] Auth state change error:', error);
            }

            // Capture auth state listener error in Sentry
            Sentry.captureException(error, {
                tags: {
                    feature: 'auth-state-listener',
                    component: 'firebase-sdk',
                },
                level: 'error',
            });

            authState.user = null;
            authState.authenticated = false;
            authState.loading = false;
            authState.error = error.message;

            // Call the callback if provided
            if (onStateChange) {
                onStateChange({
                    user: null,
                    authenticated: false,
                    loading: false,
                    error: error.message,
                });
            }
        }
    );
};

/**
 * Composable for authentication
 * @returns {object} Auth methods and state
 */
export const useAuth = () => {
    return {
        authState,
        getCurrentUser,
        requestEmailLink,
        isEmailSignInLink,
        signInWithEmail,
        setupAuthListener,
    };
};
